import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { useStoryFlash } from "@/state/storyFlash";

/**
 * StoryFlash 큐 소비자. 한 번에 한 개만 렌더, duration 경과 시 shift 로 다음 것 승격.
 *
 * <p>tier="mid": 화면 중앙 가로 배너 (폭 min(640, 80vw)). dim 배경 없음.
 * <p>tier="high": 풀스크린 dim + 중앙 대형. glyph 크게, title 크게.
 *
 * <p>App 루트에 한 번만 마운트. ToastRail 처럼.
 */
export function StoryFlashOverlay() {
    const head = useStoryFlash(s => s.queue[0]);
    const shift = useStoryFlash(s => s.shift);

    useEffect(() => {
        if (!head) return;
        const t = window.setTimeout(() => shift(), head.durationMs);
        return () => window.clearTimeout(t);
    }, [head, shift]);

    if (!head) return null;

    if (head.tier === "high") return <HighTierFlash key={head.id} entry={head} onDismiss={shift} />;
    return <MidTierFlash key={head.id} entry={head} onDismiss={shift} />;
}

function MidTierFlash({
    entry,
    onDismiss,
}: {
    entry: ReturnType<typeof useStoryFlash.getState>["queue"][number];
    onDismiss: () => void;
}) {
    const accent = entry.accent ?? tokens.color.brass;
    return (
        <div
            className="pointer-events-none fixed inset-x-0 top-[32%] flex justify-center"
            style={{ zIndex: tokens.elevation.modal }}
            onClick={onDismiss}
        >
            <AnimatePresence>
                <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, y: -16, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.36, ease: tokens.easing.decelerate }}
                    className="pointer-events-auto rounded-l px-xl py-m cursor-pointer relative overflow-hidden"
                    style={{
                        width: "min(640px, 80vw)",
                        background: `linear-gradient(180deg, ${tokens.color.ink}F0 0%, ${tokens.color.ink}D8 100%)`,
                        border: `1px solid ${accent}AA`,
                        boxShadow: `0 0 40px ${accent}55, inset 0 1px 0 ${accent}44, 0 14px 32px ${tokens.color.ink}88`,
                        backdropFilter: "blur(6px)",
                    }}
                    title="클릭하면 넘깁니다"
                >
                    {/* 2026-04-23: 좌측 accent 스트립 — Toast 와 동일 언어. */}
                    <div
                        aria-hidden
                        className="absolute inset-y-0 left-0"
                        style={{
                            width: 3,
                            background: `linear-gradient(180deg, ${accent}FF 0%, ${accent}66 100%)`,
                            boxShadow: `0 0 10px ${accent}CC`,
                        }}
                    />
                    <div className="flex items-center gap-m pl-[6px]">
                        {entry.glyph && (
                            <motion.div
                                aria-hidden
                                className="font-display shrink-0"
                                animate={{
                                    textShadow: [
                                        `0 0 22px ${accent}AA`,
                                        `0 0 40px ${accent}FF, 0 0 12px #FFF6D8AA`,
                                        `0 0 22px ${accent}AA`,
                                    ],
                                }}
                                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                                style={{
                                    color: accent,
                                    fontSize: 44,
                                    lineHeight: 1,
                                }}
                            >
                                {entry.glyph}
                            </motion.div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div
                                className="font-body"
                                style={{
                                    color: accent,
                                    fontSize: 10,
                                    letterSpacing: "0.24em",
                                    textTransform: "uppercase",
                                    opacity: 0.85,
                                    marginBottom: 2,
                                }}
                            >
                                Story Flash
                            </div>
                            <div
                                className="font-display"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: tokens.type.size.subtitle,
                                    letterSpacing: "0.04em",
                                    textShadow: `0 0 18px ${accent}66, 0 1px 0 ${tokens.color.ink}`,
                                }}
                            >
                                {entry.title}
                            </div>
                            {entry.body && (
                                <div
                                    className="mt-xs font-body"
                                    style={{
                                        color: tokens.color.parchment,
                                        fontSize: tokens.type.size.small,
                                        lineHeight: tokens.type.lineHeight.normal,
                                        opacity: 0.92,
                                    }}
                                >
                                    {entry.body}
                                </div>
                            )}
                        </div>
                    </div>
                    <motion.div
                        aria-hidden
                        className="absolute bottom-0 left-0 h-[2px] rounded-b-l"
                        initial={{ width: "100%" }}
                        animate={{ width: "0%" }}
                        transition={{ duration: entry.durationMs / 1000, ease: "linear" }}
                        style={{
                            background: `linear-gradient(90deg, ${accent}FF, ${accent}66)`,
                            boxShadow: `0 0 8px ${accent}`,
                        }}
                    />
                </motion.div>
            </AnimatePresence>
        </div>
    );
}

function HighTierFlash({
    entry,
    onDismiss,
}: {
    entry: ReturnType<typeof useStoryFlash.getState>["queue"][number];
    onDismiss: () => void;
}) {
    const accent = entry.accent ?? tokens.color.ember;
    return (
        <div
            className="fixed inset-0 flex items-center justify-center cursor-pointer"
            style={{ zIndex: tokens.elevation.modal + 2 }}
            onClick={onDismiss}
            title="클릭하면 넘깁니다"
        >
            <AnimatePresence>
                <motion.div
                    key={`${entry.id}-dim`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="absolute inset-0"
                    style={{
                        background: `radial-gradient(circle at 50% 42%, ${accent}24 0%, ${tokens.color.ink}EE 40%, ${tokens.color.ink}F8 100%)`,
                        backdropFilter: "blur(8px)",
                    }}
                />
                <motion.div
                    key={`${entry.id}-card`}
                    initial={{ opacity: 0, scale: 0.9, y: 18 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: -8 }}
                    transition={{ duration: 0.55, ease: tokens.easing.decelerate }}
                    className="relative text-center px-xl py-l pointer-events-auto"
                    style={{
                        maxWidth: 720,
                    }}
                >
                    {entry.glyph && (
                        <motion.div
                            aria-hidden
                            className="mx-auto mb-m"
                            initial={{ opacity: 0, scale: 0.7 }}
                            animate={{
                                opacity: [0.92, 1, 0.92],
                                scale: [1, 1.04, 1],
                                textShadow: [
                                    `0 0 60px ${accent}, 0 0 120px ${accent}66`,
                                    `0 0 96px ${accent}, 0 0 180px ${accent}99, 0 0 16px #FFF6D8AA`,
                                    `0 0 60px ${accent}, 0 0 120px ${accent}66`,
                                ],
                            }}
                            transition={{
                                opacity: { delay: 0.12, duration: 2.6, repeat: Infinity, ease: "easeInOut" },
                                scale: { delay: 0.12, duration: 2.6, repeat: Infinity, ease: "easeInOut" },
                                textShadow: { delay: 0.12, duration: 2.6, repeat: Infinity, ease: "easeInOut" },
                            }}
                            style={{
                                color: accent,
                                fontSize: 120,
                                lineHeight: 1,
                            }}
                        >
                            {entry.glyph}
                        </motion.div>
                    )}
                    <motion.div
                        className="font-display"
                        initial={{ opacity: 0, letterSpacing: "0.6em" }}
                        animate={{ opacity: 1, letterSpacing: tokens.type.letterSpacing.wide }}
                        transition={{ delay: 0.2, duration: 0.72, ease: tokens.easing.decelerate }}
                        style={{
                            color: tokens.color.parchment,
                            fontSize: "clamp(28px, 3.4vw, 48px)",
                            textShadow: `0 0 36px ${accent}99, 0 2px 12px ${tokens.color.ink}`,
                        }}
                    >
                        {entry.title}
                    </motion.div>
                    {entry.body && (
                        <motion.div
                            className="mt-m mx-auto font-body"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.94 }}
                            transition={{ delay: 0.55, duration: 0.6 }}
                            style={{
                                color: tokens.color.parchmentDim,
                                fontSize: tokens.type.size.body,
                                lineHeight: tokens.type.lineHeight.loose,
                                maxWidth: 620,
                                textShadow: `0 1px 6px ${tokens.color.ink}`,
                            }}
                        >
                            {entry.body}
                        </motion.div>
                    )}
                    <motion.div
                        aria-hidden
                        className="mx-auto mt-l"
                        initial={{ width: "20%", opacity: 0 }}
                        animate={{ width: "42%", opacity: 0.7 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        style={{
                            height: 1,
                            background: `linear-gradient(90deg, transparent, ${accent}AA, transparent)`,
                            boxShadow: `0 0 18px ${accent}55`,
                        }}
                    />
                    <motion.div
                        aria-hidden
                        className="mt-m font-body"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        transition={{ delay: 1.2, duration: 0.4 }}
                        style={{
                            color: tokens.color.parchmentDim,
                            fontSize: tokens.type.size.tiny,
                            letterSpacing: tokens.type.letterSpacing.wide,
                        }}
                    >
                        아무 곳이나 누르면 다음 장면으로 이어집니다
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
